import { MouseEventHandler, ReactNode } from "react";
import { Container } from "../../elements/Container";
import { ButtonVariants } from "../../shared/Button";
import { LinkButton } from "../../elements/LinkButton";
import { Image } from "../../elements/Image";
import { styles } from "./IllustratedBanner.style";
import { contentWrapperRecipe, mainImageWrapperRecipe } from "./IllustratedBanner.recipe";
import { Grid } from "../../elements/Grid";
import { calcAspectForImage } from "../../elements/Image/Image";
import { content } from "../../core/styles/content.style";
import { BulletPoints } from "./elements/BulletPoints";
import { VinSearchFooter } from "./elements/VinSearchFooter";

export interface IllustratedBannerProps {
  partnerNote?: string;
  partnerLogo?: {
    url: string;
    title?: string;
    width?: number;
    height?: number;
  };
  title: string;
  description?: ReactNode;
  ctaText: string;
  ctaUrl: string;
  additionalLinkText?: string;
  additionalLinkUrl?: string;
  vinSearch: ReactNode;
  modalText: ReactNode;
  trustIconsText?: string;
  trustIcons?: { url: string; title?: string }[];
  mainImage?: {
    url: string;
    title?: string;
    width?: number;
    height?: number;
  };
  imageSize?: string;
  ctaOnClick: MouseEventHandler;
  sampleReport?: ReactNode;
  bulletPoints?: string[];
  vinSearchPrimaryText?: string | null; // eslint-disable-line sonarjs/no-redundant-optional -- Contentful returns explicit null
  vinSearchSecondaryText?: string | null; // eslint-disable-line sonarjs/no-redundant-optional -- Contentful returns explicit null
}

export enum ImageSize {
  SMALL = "small",
  BIG = "big",
}

export const IllustratedBanner = (props: IllustratedBannerProps) => {
  const {
    partnerNote,
    partnerLogo,
    title,
    description,
    ctaText,
    ctaUrl,
    additionalLinkText,
    additionalLinkUrl,
    mainImage,
    imageSize,
    ctaOnClick,
    vinSearch,
    modalText,
    trustIconsText,
    trustIcons,
    sampleReport,
    bulletPoints = [],
    vinSearchPrimaryText,
    vinSearchSecondaryText,
  } = props;

  const getImageSizeVariant = (size?: string): ImageSize => {
    if (size === "big") return ImageSize.BIG;
    return ImageSize.SMALL;
  };

  const imageSizeVariant = getImageSizeVariant(imageSize);

  const maxImageSizes = {
    mobile: 333,
    tablet: 390,
    desktop: imageSizeVariant === ImageSize.BIG ? 641 : 496,
  };

  const aspectRatio = calcAspectForImage(mainImage);

  return (
    <>
      <div className={styles.root()}>
        <Container>
          <Grid container>
            <Grid width={6}>
              <div className={contentWrapperRecipe({ imageSize: imageSizeVariant })}>
                {partnerNote && (
                  <div className={styles.partnerNoteWrapper()}>
                    <span className={styles.partnerNoteText()}>{partnerNote}</span>
                    {partnerLogo && (
                      <img src={partnerLogo.url} alt={partnerLogo.title || ""} className={styles.partnerLogo()} />
                    )}
                  </div>
                )}
                <div className={styles.titleWrapper()}>
                  <h1 className={styles.titleText()}>{title}</h1>
                </div>
                {description && bulletPoints.length === 0 && <div className={styles.description()}>{description}</div>}
                {bulletPoints.length > 0 && <BulletPoints bulletPoints={bulletPoints} />}
                <div className={styles.vinSearchWrapper(!!vinSearch)}>{vinSearch}</div>
                <div>
                  {vinSearch ? (
                    <VinSearchFooter
                      primaryText={vinSearchPrimaryText}
                      secondaryText={vinSearchSecondaryText}
                      sampleReport={sampleReport}
                      modalText={modalText}
                      additionalLinkText={additionalLinkText}
                      additionalLinkUrl={additionalLinkUrl}
                      ctaText={ctaText}
                      ctaUrl={ctaUrl}
                      ctaOnClick={ctaOnClick}
                    />
                  ) : (
                    <>
                      {sampleReport && <div className={styles.sampleReport()}>{sampleReport}</div>}
                      {modalText && <div className={styles.linkWrapper()}>{modalText}</div>}
                      {additionalLinkText && (
                        <div className={styles.additionalLinkWrapper()}>
                          <LinkButton
                            href={additionalLinkUrl}
                            openInNewTab
                            rel="noreferrer"
                            variant={ButtonVariants.Text}
                          >
                            {additionalLinkText}
                          </LinkButton>
                        </div>
                      )}
                      {ctaText && (
                        <div className={styles.cta(!!vinSearch)}>
                          <LinkButton
                            testSelector="campaign-banner-cta"
                            href={ctaUrl}
                            onClick={ctaOnClick}
                            variant={vinSearch ? ButtonVariants.Text : ButtonVariants.Standard}
                          >
                            {ctaText}
                          </LinkButton>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Grid>
            <Grid width={6}>
              <div
                className={mainImageWrapperRecipe({ imageSize: imageSizeVariant })}
                style={{ aspectRatio: aspectRatio || undefined }}
              >
                <Image
                  image={mainImage}
                  fetchPriorityHigh
                  loadEagerly
                  srcSizes={[
                    maxImageSizes.mobile,
                    maxImageSizes.tablet,
                    maxImageSizes.desktop,
                    maxImageSizes.mobile * 2,
                    maxImageSizes.tablet * 2,
                    maxImageSizes.desktop * 2,
                  ]}
                  defaultSrcSize={1300}
                  sizes={`(min-width: ${content.maxContentWidth}px) ${maxImageSizes.desktop}px,(min-width: ${content.tabletWidth}px) ${maxImageSizes.tablet}px, ${maxImageSizes.mobile}px`}
                />
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>
      {trustIcons && trustIconsText && (
        <div className={styles.trustIcons()}>
          <div>{trustIconsText}</div>
          {trustIcons.map((icon) => (
            <img key={icon.title} src={icon.url + "?w=200"} alt={icon.title || ""} />
          ))}
        </div>
      )}
    </>
  );
};
